import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, RefreshCw, ImageUp, ShieldCheck, AlertTriangle, ZoomIn, TrendingUp, TrendingDown, Globe, Share2, Link2, Hash } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import ConditionSlider from "../components/ConditionSlider";
import CardCopiesPanel from "../components/CardCopiesPanel";
import AdditionalPhotos from "../components/AdditionalPhotos";
import PossibleMatches from "../components/PossibleMatches";
import PriceHistoryChart from "../components/PriceHistoryChart";
import RecentSales from "../components/RecentSales";
import { EbayLiveListings } from "../components/EbayLiveListings";
import ImageOptionsDialog from "../components/ImageOptionsDialog";
import FixMatchDialog from "../components/FixMatchDialog";
import ImageLightbox from "../components/ImageLightbox";
import CompleteSaleDialog from "../components/CompleteSaleDialog";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import { useCurrency } from "../context/CurrencyContext";
import { cardTotalValue } from "../lib/conditions";

export default function CardDetail() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { format, rate, symbol } = useCurrency();
  const referenceInputRef = useRef(null);
  const capturedInputRef = useRef(null);
  const [card, setCard] = useState(null);
  const [collections, setCollections] = useState([]);
  const [serial, setSerial] = useState("");
  const [context, setContext] = useState("");
  const [targetPrice, setTargetPrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [rescanning, setRescanning] = useState(false);
  const [repricing, setRepricing] = useState(false);
  const [uploadingReference, setUploadingReference] = useState(false);
  const [uploadingCaptured, setUploadingCaptured] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [dupPrompt, setDupPrompt] = useState(null);
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);

  useEffect(() => {
    api.get(`/cards/${cardId}`).then((res) => {
      setCard(res.data);
      setSerial(res.data.serial_number || "");
      setContext(res.data.user_context || "");
      setTargetPrice(res.data.target_price != null ? String(res.data.target_price) : "");
      setQuantity(String(res.data.quantity || 1));
    });
    api.get("/collections").then((res) => setCollections(res.data));
  }, [cardId]);

  const patchCard = async (fields) => {
    const res = await api.patch(`/cards/${cardId}`, fields);
    setCard(res.data);
  };

  useEffect(() => {
    if (!card) return;
    const usd = card.sale_status === "under_offer" ? card.offer_price
      : card.sale_status === "sold_stc" ? card.sold_price : null;
    setSalePrice(usd != null ? (usd * rate).toFixed(2) : "");
  }, [card?.sale_status, card?.offer_price, card?.sold_price, rate]);

  const saveSalePrice = () => {
    const field = card.sale_status === "under_offer" ? "offer_price" : "sold_price";
    const existing = card[field];
    if (salePrice === "") {
      if (existing != null) {
        patchCard({ [field]: 0 });
        toast.success(field === "offer_price" ? "Offer price removed" : "Sold price removed");
      }
      return;
    }
    const parsed = parseFloat(salePrice);
    if (isNaN(parsed)) return;
    if (parsed <= 0) {
      setSalePrice("");
      if (existing != null) {
        patchCard({ [field]: 0 });
        toast.success(field === "offer_price" ? "Offer price removed" : "Sold price removed");
      }
      return;
    }
    const usd = parsed / (rate || 1);
    if (existing != null && Math.abs(usd - existing) < 0.005) return;
    patchCard({ [field]: usd });
    toast.success(field === "offer_price" ? "Offer price saved" : "Sold price saved");
  };

  const saveSerial = () => {
    const v = serial.trim();
    if (v === (card.serial_number || "")) return;
    patchCard({ serial_number: v });
    toast.success(v ? `Serial number set to ${v}` : "Serial number removed");
  };

  const handleDelete = async () => {
    await api.delete(`/cards/${cardId}`);
    toast.success("Card removed from binder");
    navigate("/binder");
  };

  const handleRescan = async () => {
    setRescanning(true);
    try {
      if (context !== (card.user_context || "")) {
        await api.patch(`/cards/${cardId}`, { user_context: context });
      }
      const res = await api.post("/cards/rescan", { card_ids: [cardId] });
      setCard(res.data[0]);
      toast.success("Re-scanned with AI");
      checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-scan failed. Please try again.");
    } finally {
      setRescanning(false);
    }
  };

  const handleReprice = async () => {
    setRepricing(true);
    try {
      const res = await api.post("/cards/reprice", { card_ids: [cardId] });
      setCard(res.data[0]);
      toast.success("Market price refreshed");
    } catch {
      toast.error("Price re-scan failed. Please try again.");
    } finally {
      setRepricing(false);
    }
  };

  const handleReferenceUpload = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setUploadingReference(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`/cards/${cardId}/reference-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCard(res.data);
      toast.success("Your reference image is now shown instead of the web match");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploadingReference(false);
    }
  };

  const handleCapturedUpload = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setUploadingCaptured(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`/cards/${cardId}/captured-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCard(res.data);
      toast.success("Photo saved — AI identification is now available for this card");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploadingCaptured(false);
    }
  };

  const saveTargetPrice = () => {
    if (targetPrice === "") return;
    const parsed = parseFloat(targetPrice);
    if (isNaN(parsed) || parsed === card.target_price) return;
    patchCard({ target_price: parsed });
  };

  const saveQuantity = () => {
    const parsed = parseInt(quantity, 10);
    if (isNaN(parsed) || parsed < 1) {
      setQuantity(String(card.quantity || 1));
      return;
    }
    const current = card.quantity || 1;
    if (parsed === current) return;
    if (parsed > current) {
      setDupPrompt(parsed);
      return;
    }
    patchCard({ quantity: parsed });
  };

  const applyQuantity = async (mode) => {
    await patchCard({ quantity: dupPrompt, duplicate_mode: mode });
    setDupPrompt(null);
    toast.success(mode === "separate" ? "Copies split out — set each one's condition below" : "Stacked with the original");
  };

  if (!card) {
    return <div className="min-h-screen bg-[#05060f] flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-[#A78BFA] rounded-full animate-spin" /></div>;
  }

  const referenceLabel =
    card.reference_image_source === "user_uploaded" ? "Your Reference (Uploaded)"
    : card.reference_image_source === "user_selected" ? "Your Pick (Confirmed)"
    : card.reference_image_source === "official_db" ? "Official Match (Database)"
    : card.reference_image_source === "official_probable" ? "Official Match (Probable)"
    : card.reference_image_source === "scraper_probable" ? "AI Match (Probable)"
    : "AI Match (Web)";

  return (
    <div className="min-h-screen bg-[#05060f] text-white px-6 py-12">
      <div className="max-w-5xl mx-auto">
        <button data-testid="card-detail-back-button" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-6 h-fit">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Your Snapshot</p>
                {card.captured_image_url ? (
                  <button
                    type="button"
                    data-testid="card-detail-captured-image-button"
                    onClick={() => setLightbox({ src: `${process.env.REACT_APP_BACKEND_URL}${card.captured_image_url}`, alt: `${card.name} — your video snapshot`, label: "Your Snapshot" })}
                    className="relative group w-full block cursor-zoom-in"
                    title="Click to enlarge"
                  >
                    <img data-testid="card-detail-captured-image" src={`${process.env.REACT_APP_BACKEND_URL}${card.captured_image_url}`} alt="captured" className="w-full aspect-[3/4] object-cover rounded-xl border border-white/10 transition-opacity duration-200 group-hover:opacity-85" />
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      <span className="bg-black/70 border border-white/20 rounded-full p-2.5"><ZoomIn className="w-5 h-5 text-white" /></span>
                    </span>
                  </button>
                ) : (
                  <div data-testid="card-detail-captured-empty" className="w-full aspect-[3/4] flex items-center justify-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-xl text-center px-4">No photo yet</div>
                )}
                <input
                  ref={capturedInputRef}
                  type="file"
                  accept="image/*"
                  data-testid="card-detail-captured-upload-input"
                  className="hidden"
                  onChange={(e) => { handleCapturedUpload(e.target.files); e.target.value = ""; }}
                />
                <button
                  data-testid="card-detail-captured-upload-button"
                  onClick={() => capturedInputRef.current?.click()}
                  disabled={uploadingCaptured}
                  className="mt-2 flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-white transition-colors disabled:opacity-40"
                >
                  <ImageUp className="w-3.5 h-3.5" /> {uploadingCaptured ? "Uploading..." : card.captured_image_url ? "Replace photo" : "Add photo for AI"}
                </button>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  {referenceLabel}
                </p>
                {card.reference_image_url ? (
                  <button
                    type="button"
                    data-testid="card-detail-reference-image-button"
                    onClick={() => setLightbox({ src: pcImageUrl(card.reference_image_url, "1600"), alt: `${card.name} — reference image`, label: referenceLabel })}
                    className="relative group w-full block cursor-zoom-in"
                    title="Click to enlarge"
                  >
                    <img data-testid="card-detail-reference-image" src={pcImageUrl(card.reference_image_url, "1600")} onError={pcImageFallback} alt="reference" className="w-full aspect-[3/4] object-cover rounded-xl border border-white/10 transition-opacity duration-200 group-hover:opacity-85" />
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      <span className="bg-black/70 border border-white/20 rounded-full p-2.5"><ZoomIn className="w-5 h-5 text-white" /></span>
                    </span>
                  </button>
                ) : (
                  <div data-testid="card-detail-reference-empty" className="w-full aspect-[3/4] flex items-center justify-center text-xs text-zinc-600 border border-white/10 rounded-xl text-center px-4">
                    {card.reference_image_source === "user_removed" ? "Match removed — your scan is the default" : "No match found"}
                  </div>
                )}
                {card.reference_image_url && (
                  card.match_verified ? (
                    <p data-testid="card-detail-verified-badge" className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400">
                      <ShieldCheck className="w-3.5 h-3.5" /> Visually verified match
                    </p>
                  ) : (
                    <p data-testid="card-detail-unverified-badge" className="mt-2 flex items-center gap-1.5 text-xs text-[#F59E0B]">
                      <AlertTriangle className="w-3.5 h-3.5" /> {String(card.reference_image_source || "").endsWith("_probable") ? "Probable match — identity-based, not visually verified" : "Match not visually verified"}
                    </p>
                  )
                )}
                <input
                  ref={referenceInputRef}
                  type="file"
                  accept="image/*"
                  data-testid="card-detail-reference-upload-input"
                  className="hidden"
                  onChange={(e) => { handleReferenceUpload(e.target.files); e.target.value = ""; }}
                />
                <button
                  data-testid="card-detail-reference-upload-button"
                  onClick={() => referenceInputRef.current?.click()}
                  disabled={uploadingReference}
                  className="mt-2 flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-white transition-colors disabled:opacity-40"
                >
                  <ImageUp className="w-3.5 h-3.5" /> {uploadingReference ? "Uploading..." : "Match wrong? Upload your own"}
                </button>
                <div className="mt-2">
                  <FixMatchDialog
                    card={card}
                    onUpdated={(updated) => setCard(updated)}
                    trigger={<><Link2 className="w-3.5 h-3.5" /> Paste image link or remove match</>}
                    triggerTestId="card-detail-fix-match-button"
                  />
                </div>
                <a
                  data-testid="card-detail-web-search-button"
                  href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent([card.name, card.set_name, card.card_number, "card"].filter(Boolean).join(" "))}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-white transition-colors"
                >
                  <Globe className="w-3.5 h-3.5" /> Search the web for this card
                </a>
                <ImageOptionsDialog card={card} onSelected={(updated) => setCard(updated)} />
              </div>
            </div>
            <PossibleMatches card={card} onSelected={(updated) => setCard(updated)} />
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <h1 data-testid="card-detail-name" className="text-2xl font-display font-bold">{card.name}</h1>
                {card.card_number && (
                  <span data-testid="card-detail-title-number" className="text-xl font-display font-semibold text-zinc-500">
                    {String(card.card_number).includes("/") ? card.card_number : `#${card.card_number}`}
                  </span>
                )}
                {card.serial_number && (
                  <span data-testid="card-detail-serial-badge" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">
                    <Hash className="w-3 h-3" /> {card.serial_number}
                  </span>
                )}
              </div>
              <div className="text-sm text-zinc-500 flex items-center gap-1.5 flex-wrap">
                <span>{card.set_name || "Unknown set"}</span>
                {card.card_number && <span data-testid="card-detail-number">· No. {card.card_number}</span>}
                {card.variant && card.variant.toLowerCase() !== (card.rarity || "").toLowerCase() && <span>· {card.variant}</span>}
                <span>· <span className="capitalize">{card.rarity || "rarity unknown"}</span></span>
              </div>
              {card.is_graded && (
                <span
                  data-testid="card-detail-grade-chip"
                  className="inline-block mt-2 mr-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-sky-400/30 bg-sky-500/10 text-sky-300"
                >
                  Graded · {[card.grading_company, card.grade].filter(Boolean).join(" ") || "slab"}
                </span>
              )}
              {card.id_confidence && (
                <span
                  data-testid="card-detail-confidence-chip"
                  className={`inline-block mt-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                    card.id_confidence === "high"
                      ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
                      : card.id_confidence === "medium"
                        ? "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#F59E0B]"
                        : "border-red-400/30 bg-red-500/10 text-red-300"
                  }`}
                >
                  AI confidence: {card.id_confidence}
                </span>
              )}
              <button
                type="button"
                data-testid="card-detail-share-button"
                onClick={async () => {
                  try {
                    const res = await api.post(`/cards/${card.id}/share`);
                    const url = `${window.location.origin}/api/cardshare/${res.data.token}/share`;
                    await navigator.clipboard.writeText(url);
                    toast.success("Flex link copied — paste it in Discord or X and it unfurls into a preview");
                  } catch {
                    toast.error("Couldn't create the share link. Please try again.");
                  }
                }}
                className="inline-flex items-center gap-1 mt-2 ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#A78BFA]/30 bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/20 transition-colors"
              >
                <Share2 className="w-3 h-3" /> Share this card
              </button>
            </div>

            <div data-testid="card-detail-serial-section" className="border border-white/10 bg-[#111113] rounded-xl p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <label htmlFor="serial-input" className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                  <Hash className="w-3 h-3 text-[#F59E0B]" /> Serial / print-run number
                </label>
                <p className="text-[11px] text-zinc-600 leading-snug mt-1">
                  For numbered or short-print cards (e.g. 55/75). Add it here if the AI missed it — often on the back or hard to read. This is extra info and won't change the card's identity.
                </p>
              </div>
              <Input
                id="serial-input"
                data-testid="card-detail-serial-input"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                onBlur={saveSerial}
                onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                placeholder="e.g. 55/75"
                className="w-28 shrink-0 bg-black/40 border-white/10 text-right font-mono-plex text-[#F59E0B] font-semibold placeholder:text-zinc-700"
              />
            </div>

            <div className="border border-white/10 bg-[#111113] rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-zinc-500">Pricing</span>
                <Button
                  data-testid="card-detail-reprice-button"
                  onClick={handleReprice}
                  disabled={repricing}
                  variant="ghost"
                  size="sm"
                  className="text-[#A78BFA] hover:bg-[#A78BFA]/10 rounded-full h-7 px-3 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${repricing ? "animate-spin" : ""}`} />
                  {repricing ? "Re-scanning..." : "Re-scan market price"}
                </Button>
              </div>

              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="text-sm text-zinc-400">Market Value</span>
                <span data-testid="card-detail-current-price" className={`font-mono-plex font-bold min-w-0 break-all ${card.current_price != null ? `text-2xl sm:text-3xl ${["estimate_similar", "ai_estimate"].includes(card.price_source) ? "text-[#F59E0B]" : "text-[#A78BFA]"}` : "text-sm text-zinc-500"}`}>
                  {card.current_price != null ? `${["estimate_similar", "ai_estimate"].includes(card.price_source) ? "~" : ""}${format(card.current_price)}` : "No price data available"}
                </span>
              </div>
              <p className="text-xs text-zinc-500">
                {card.base_price_nm != null ? `Near Mint baseline: ${format(card.base_price_nm)} · ` : ""}
                {card.price_source === "sold_comps"
                  ? "median of real eBay sold prices — see Recent Sales below"
                  : card.price_source === "market_data_scrape"
                  ? `from ${card.matched_listings?.length || 0} matched live listings`
                  : card.price_source === "manual"
                    ? "price set manually by you"
                    : card.price_source === "estimate_similar"
                    ? `estimated from ${card.matched_listings?.length || 0} similar listing${(card.matched_listings?.length || 0) === 1 ? "" : "s"} — no exact match verified yet`
                    : card.price_source === "ai_estimate"
                      ? "AI-estimated value — no market listings found for this card"
                      : card.price_source === "rate_limited"
                        ? "market data source is busy right now — try 'Re-scan market price' in a few minutes"
                        : card.price_source === "credit_limit_reached"
                          ? "not yet scanned — you ran out of credits during upload"
                          : "no pricing data found for this card yet"}
              </p>

              {(card.sold_raw_price != null || card.sold_graded_price != null) && (
                <div data-testid="card-detail-sold-prices" className="flex items-center gap-2 flex-wrap pt-1">
                  {card.sold_raw_price != null && (
                    <span data-testid="card-detail-raw-price" className={`text-xs font-mono-plex px-2.5 py-1 rounded-full border ${!card.is_graded ? "border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA]" : "border-white/10 bg-white/5 text-zinc-300"}`}>
                      Raw · {format(card.sold_raw_price)}
                    </span>
                  )}
                  {card.sold_graded_price != null && (
                    <span data-testid="card-detail-graded-price" className={`text-xs font-mono-plex px-2.5 py-1 rounded-full border ${card.is_graded ? "border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA]" : "border-white/10 bg-white/5 text-zinc-300"}`}>
                      Graded · {format(card.sold_graded_price)}
                    </span>
                  )}
                  {(() => {
                    const trend = card.is_graded ? card.sold_graded_trend : card.sold_raw_trend;
                    if (trend == null || Math.abs(trend) < 2) return <span className="text-[10px] text-zinc-600">from last-sold data</span>;
                    return (
                      <span
                        data-testid="card-detail-trend"
                        className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${trend > 0 ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300" : "border-red-400/30 bg-red-500/10 text-red-300"}`}
                      >
                        {trend > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                        {trend > 0 ? "+" : ""}{trend}% sold-price trend
                      </span>
                    );
                  })()}
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="text-sm text-zinc-400">Quantity owned</span>
                <Input
                  data-testid="card-detail-quantity-input"
                  type="number"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  onBlur={saveQuantity}
                  className="w-24 bg-black/40 border-white/10 text-right font-mono-plex font-semibold"
                />
              </div>
              {(card.quantity || 1) > 1 && card.current_price != null && (
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-zinc-400">Combined value (×{card.quantity})</span>
                  <span data-testid="card-detail-combined-value" className={`font-mono-plex font-bold text-xl ${["estimate_similar", "ai_estimate"].includes(card.price_source) ? "text-[#F59E0B]" : "text-[#A78BFA]"}`}>
                    {["estimate_similar", "ai_estimate"].includes(card.price_source) ? "~" : ""}{format(cardTotalValue(card))}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="text-sm text-zinc-400">Your Sale Price (USD)</span>
                <div className="flex items-center gap-1">
                  <span className="text-zinc-500 font-mono-plex">$</span>
                  <Input
                    data-testid="card-detail-target-price-input"
                    type="number"
                    step="0.01"
                    value={targetPrice}
                    onChange={(e) => setTargetPrice(e.target.value)}
                    onBlur={saveTargetPrice}
                    placeholder={card.current_price != null ? card.current_price.toFixed(2) : "0.00"}
                    className="w-24 bg-black/40 border-white/10 text-right font-mono-plex text-[#F59E0B] font-semibold"
                  />
                </div>
              </div>

              <div className="pt-2 border-t border-white/10">
                <span className="text-sm text-zinc-400 block mb-2">Sale status</span>
                <div className="flex gap-2 flex-wrap" data-testid="card-detail-sale-status">
                  {[["none", "Available"], ["under_offer", "Under Offer"], ["sold_stc", "Sold STC"]].map(([val, label]) => {
                    const active = (card.sale_status || "none") === val;
                    const activeClass = val === "under_offer"
                      ? "border-[#7C3AED] text-white bg-[#7C3AED]/80"
                      : val === "sold_stc"
                        ? "border-red-500 text-white bg-red-600/80"
                        : "border-emerald-400/60 text-emerald-300 bg-emerald-500/10";
                    return (
                      <button
                        key={val}
                        data-testid={`sale-status-${val}`}
                        onClick={() => patchCard({ sale_status: val })}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? activeClass : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {(card.sale_status === "under_offer" || card.sale_status === "sold_stc") && (
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm text-zinc-400">
                      {card.sale_status === "under_offer" ? "Offer price" : "Sold price"}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-zinc-500 font-mono-plex">{symbol}</span>
                      <Input
                        data-testid="card-detail-sale-price-input"
                        type="number"
                        step="0.01"
                        value={salePrice}
                        onChange={(e) => setSalePrice(e.target.value)}
                        onBlur={saveSalePrice}
                        placeholder="0.00"
                        className={`w-24 bg-black/40 border-white/10 text-right font-mono-plex font-semibold ${card.sale_status === "under_offer" ? "text-[#A78BFA]" : "text-red-400"}`}
                      />
                    </div>
                  </div>
                )}
                {(card.sale_status === "under_offer" || card.sale_status === "sold_stc") && (
                  <Button
                    data-testid="card-detail-complete-sale-button"
                    variant="ghost"
                    onClick={() => setSaleDialogOpen(true)}
                    className="w-full mt-3 rounded-full bg-emerald-500/10 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200"
                  >
                    <ShieldCheck className="w-4 h-4 mr-1.5" /> Complete sale — log it in your Sales Ledger
                  </Button>
                )}
                {card.sale_status === "sold_stc" && (
                  <p className="text-[10px] text-zinc-600 mt-1.5">Sold subject to completion — the sold price shows on the card overlay. Remove the card once the sale completes.</p>
                )}
                {card.sale_status === "under_offer" && (
                  <p className="text-[10px] text-zinc-600 mt-1.5">The offer price shows on the purple overlay on your binder and public pages.</p>
                )}
              </div>

              {card.is_graded ? (
                <div data-testid="card-detail-graded-condition" className="pt-2 border-t border-white/10 text-xs text-zinc-400">
                  <span className="text-sky-300 font-semibold">{[card.grading_company, card.grade].filter(Boolean).join(" ") || "Graded slab"}</span> — condition
                  is fixed by the grade on the slab label, so condition sliders don't apply. Value tracks sales of the same grade.
                </div>
              ) : card.copy_conditions?.length > 1 ? (
                <CardCopiesPanel
                  card={card}
                  format={format}
                  onUpdate={(copies) => patchCard(copies ? { copy_conditions: copies } : { duplicate_mode: "merge" })}
                />
              ) : (
                <ConditionSlider condition={card.condition} onChange={(condition) => patchCard({ condition })} />
              )}
            </div>

            <PriceHistoryChart cardId={card.id} refreshKey={`${card.base_price_nm}-${card.updated_at}`} />

            <RecentSales sales={card.sold_comps} format={format} />

            <EbayLiveListings card={card} format={format} />

            <div className="border border-white/10 bg-[#111113] rounded-xl p-6">
              <AdditionalPhotos
                cardId={card.id}
                images={card.additional_images || []}
                onChange={(images) => setCard((c) => ({ ...c, additional_images: images }))}
              />
            </div>

            <div className="border border-white/10 bg-[#111113] rounded-xl p-6 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-widest text-zinc-500">
                  Identification wrong? Add context
                </label>
              </div>
              <Textarea
                data-testid="card-detail-context-textarea"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                onBlur={() => context !== (card.user_context || "") && patchCard({ user_context: context })}
                placeholder="e.g. This is the Darth Vader promo from Star Wars Card Trader, not what's shown — look for that exact set."
                className="bg-black/40 border-white/10 min-h-[80px] text-sm"
              />
              {card.captured_image_url ? (
                <Button
                  data-testid="card-detail-rescan-button"
                  onClick={handleRescan}
                  disabled={rescanning}
                  variant="outline"
                  className="border-[#A78BFA]/40 text-[#A78BFA] hover:bg-[#A78BFA]/10 rounded-full disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${rescanning ? "animate-spin" : ""}`} />
                  {rescanning ? "Re-scanning..." : "Re-scan with AI"}
                </Button>
              ) : (
                <p data-testid="card-detail-rescan-needs-photo" className="text-xs text-zinc-500">
                  Add a photo of this card (left) to unlock AI identification.
                </p>
              )}
            </div>

            <div className="border border-white/10 bg-[#111113] rounded-xl p-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Game type</label>
                <Select value={card.game_type} onValueChange={(v) => patchCard({ game_type: v })}>
                  <SelectTrigger data-testid="card-detail-gametype-select" className="bg-black/40 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pokemon">Pokemon</SelectItem>
                    <SelectItem value="mtg">Magic: The Gathering</SelectItem>
                    <SelectItem value="sports">Sports</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Card language</label>
                <Select value={card.language || "english"} onValueChange={(v) => patchCard({ language: v })}>
                  <SelectTrigger data-testid="card-detail-language-select" className="bg-black/40 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="japanese">Japanese</SelectItem>
                    <SelectItem value="chinese">Chinese</SelectItem>
                    <SelectItem value="korean">Korean</SelectItem>
                    <SelectItem value="german">German</SelectItem>
                    <SelectItem value="french">French</SelectItem>
                    <SelectItem value="spanish">Spanish</SelectItem>
                    <SelectItem value="italian">Italian</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-zinc-600 mt-1.5">Used to match the correct-language version when searching prices and images — re-scan after changing.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Collection</label>
                <Select value={card.collection_id || "none"} onValueChange={(v) => patchCard({ collection_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="card-detail-collection-select" className="bg-black/40 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Uncategorized</SelectItem>
                    {collections.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button data-testid="card-detail-delete-button" variant="outline" onClick={handleDelete} className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full">
              <Trash2 className="w-4 h-4 mr-2" /> Remove from binder
            </Button>
          </div>
        </div>
      </div>
      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} label={lightbox.label} onClose={() => setLightbox(null)} />}
      <CompleteSaleDialog
        card={card}
        open={saleDialogOpen}
        onOpenChange={setSaleDialogOpen}
        onCompleted={() => navigate("/sales")}
      />
      <Dialog
        open={dupPrompt != null}
        onOpenChange={(open) => {
          if (!open) {
            setQuantity(String(card.quantity || 1));
            setDupPrompt(null);
          }
        }}
      >
        <DialogContent data-testid="duplicate-choice-dialog" className="bg-[#111113] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Collected a duplicate?</DialogTitle>
            <DialogDescription className="text-zinc-400">
              You can track each copy separately with its own condition (and its own value), or if
              they're all the same condition, just stack them together as ×{dupPrompt}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              data-testid="duplicate-separate-button"
              onClick={() => applyQuantity("separate")}
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full font-semibold"
            >
              Track separately — own condition per copy
            </Button>
            <Button
              data-testid="duplicate-stack-button"
              variant="outline"
              onClick={() => applyQuantity("stack")}
              className="border-white/20 hover:bg-white/5 rounded-full"
            >
              Stack — same condition as the original
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
